const STICKY_BREAKPOINT = 990;

function getZoomAdjustedViewportWidth() {
    if (typeof window === 'undefined') {
        return 0;
    }

    const viewport = window.visualViewport;
    if (viewport && typeof viewport.width === 'number' && viewport.width > 0) {
        return Math.round(viewport.width);
    }

    const doc = document.documentElement;
    return window.innerWidth || (doc && doc.clientWidth) || 0;
}

class StickyElementsController {
    constructor() {
        // Check if viewport meets sticky criteria before initializing
        const isDesktopCheck = this.isDesktop();

        if (!isDesktopCheck) {
            return;
        }

        this.container = document.querySelector('.custom-product-container');
        this.tabs = document.querySelector('.custom-product-tabs');
        this.productInfo = document.querySelector('.custom-product-info');
        this.title = document.querySelector('.product-title-bar .collection-hero__title-wrapper') || document.querySelector('.collection-hero__title-wrapper');
        // The sticky wrapper set by theme.liquid (contains the title)
        this.titleBar = document.querySelector('.product-title-bar');

        if (!this.container || !this.tabs || !this.productInfo) {
            return;
        }

        // Clear any inline styles from previous sessions before initialization
        // This ensures we start fresh and read the CSS defaults
        if (this.tabs) {
            this.tabs.removeAttribute('style');
            this.tabs.classList.remove('positioned-absolute');
        }
        if (this.productInfo) {
            this.productInfo.removeAttribute('style');
            this.productInfo.classList.remove('positioned-absolute');
        }
        if (this.title) {
            // Save the sticky positioning set by theme.liquid before clearing
            // (theme.liquid moves the title bar to body level and sets position: sticky
            //  so the breadcrumb rubber-bands on overscroll like the header)
            this._themeLiquidBarPosition = this.titleBar ? this.titleBar.style.position : null;
            this._themeLiquidBarTop = this.titleBar ? this.titleBar.style.top : null;
            this._themeLiquidBarZIndex = this.titleBar ? this.titleBar.style.zIndex : null;
            this._themeLiquidBarMarginBottom = this.titleBar ? this.titleBar.style.marginBottom : null;

            this.title.removeAttribute('style');
            this.title.classList.remove('positioned-absolute');
        }

        // Force reflow after clearing styles
        if (this.tabs) this.tabs.offsetHeight;
        if (this.productInfo) this.productInfo.offsetHeight;
        if (this.title) this.title.offsetHeight;

        this.isTabsSticky = true;
        this.isProductInfoSticky = true;
        this.isTitleSticky = true;

        this.captureOriginalStyles();


        // Store the intended fixed positions for reference
        // Get the actual positions of elements when they're in their natural state
        const tabsRect = this.tabs.getBoundingClientRect();
        const productInfoRect = this.productInfo.getBoundingClientRect();

        this.tabsFixedPosition = tabsRect.top;
        this.productInfoFixedPosition = productInfoRect.top;

        // Store title's initial position if it exists
        if (this.title) {
            // The title wrapper uses its CSS defaults (margin-top, mix-blend-mode, etc.)
            // while .product-title-bar (parent) is sticky at body level.
            // No need to set position on the title wrapper itself.
            this.title.classList.remove('positioned-absolute');

            // Force a reflow to ensure styles are applied
            this.title.offsetHeight;

            // Measure the actual visual position of the title
            const titleRect = this.title.getBoundingClientRect();
            this.titleFixedPosition = titleRect.top;
        }

        this.updateFixedReferencePositions();

        this.bindEvents();

        // Defer the initial position check so that other DOMContentLoaded
        // handlers (e.g. CustomProductGallery.checkInitialScrollPosition)
        // finish settling gallery heights first.  All DOMContentLoaded
        // callbacks complete before the first rAF fires, so two frames
        // guarantee the layout is fully resolved.  This replaces the old
        // "skip checkPositions entirely" approach which left the breadcrumb
        // and tabs stuck in their sticky/fixed CSS position when the browser
        // restored a scroll‑to‑bottom on reload.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.checkPositions();
            });
        });
    }

    isDesktop() {
        // Check for desktop based on multiple criteria
        const screenWidth = getZoomAdjustedViewportWidth();
        return screenWidth >= STICKY_BREAKPOINT;
    }

    bindEvents() {
        // Bind methods to this instance so we can remove them later
        this.boundCheckPositions = this.checkPositions.bind(this);
        this.boundHandleResize = this.handleResize.bind(this);
        this._stickyTicking = false;

        const onScroll = () => {
            if (!this._stickyTicking) {
                this._stickyTicking = true;
                requestAnimationFrame(() => {
                    this.boundCheckPositions();
                    this._stickyTicking = false;
                });
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', this.boundHandleResize, { passive: false });
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.boundHandleResize, { passive: true });
        }
    }

    handleResize() {
        this.resetElementsToCssDefaults();
        this.captureOriginalStyles();

        // Force a reflow to ensure updated CSS values apply before measuring
        if (this.title) this.title.offsetHeight;
        if (this.tabs) this.tabs.offsetHeight;
        if (this.productInfo) this.productInfo.offsetHeight;

        this.updateFixedReferencePositions();

        this.checkPositions();
    }

    clearInlineStyles(element, properties = []) {
        if (!element) return;
        if (!properties.length) {
            element.removeAttribute('style');
            return;
        }
        properties.forEach((property) => {
            element.style[property] = '';
        });
    }

    resetElementsToCssDefaults() {
        if (this.tabs) {
            this.clearInlineStyles(this.tabs, ['position', 'top', 'bottom', 'left', 'right', 'transform', 'transition', 'animation']);
            this.tabs.classList.remove('positioned-absolute');
            this.isTabsSticky = true;
        }

        if (this.productInfo) {
            this.clearInlineStyles(this.productInfo, ['position', 'top', 'bottom', 'left', 'right', 'transform', 'transition', 'animation', 'willChange', 'backfaceVisibility']);
            this.productInfo.classList.remove('positioned-absolute');
            this.isProductInfoSticky = true;
        }

        if (this.title) {
            this.clearInlineStyles(this.title, ['position', 'top', 'bottom', 'left', 'right', 'transform', 'transition', 'animation', 'padding', 'zIndex', 'mixBlendMode', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft']);
            this.title.classList.remove('positioned-absolute');
            this.title.classList.remove('breadcrumb--scrolled-past');
            this.isTitleSticky = true;
        }

        // Restore titleBar to sticky at top (theme.liquid default)
        if (this.titleBar) {
            this.titleBar.style.position = 'sticky';
            this.titleBar.style.top = '0px';
        }
    }

    captureOriginalStyles() {
        if (this.tabs) {
            const tabsStyles = window.getComputedStyle(this.tabs);
            this.originalTabsStyles = {
                position: tabsStyles.position,
                top: tabsStyles.top,
                bottom: tabsStyles.bottom,
                left: tabsStyles.left,
                right: tabsStyles.right,
                transform: tabsStyles.transform
            };
        }

        if (this.productInfo) {
            const productInfoStyles = window.getComputedStyle(this.productInfo);
            this.originalProductInfoStyles = {
                position: productInfoStyles.position,
                top: productInfoStyles.top,
                bottom: productInfoStyles.bottom,
                left: productInfoStyles.left,
                right: productInfoStyles.right,
                transform: productInfoStyles.transform
            };
        }

        if (this.title) {
            const titleStyles = window.getComputedStyle(this.title);
            this.originalTitleStyles = {
                position: titleStyles.position,
                top: titleStyles.top,
                bottom: titleStyles.bottom,
                left: titleStyles.left,
                right: titleStyles.right,
                transform: titleStyles.transform,
                marginTop: titleStyles.marginTop,
                marginRight: titleStyles.marginRight,
                marginBottom: titleStyles.marginBottom,
                marginLeft: titleStyles.marginLeft,
                padding: titleStyles.padding,
                mixBlendMode: titleStyles.mixBlendMode || ''
            };
        }
    }

    isElementVisible(element) {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
    }

    getActiveMediaElement() {
        const candidates = [
            '.custom-image-gallery',
            '.mobile-slider-container',
            '.mobile-image-slider'
        ];

        for (const selector of candidates) {
            const el = this.container.querySelector(selector);
            if (this.isElementVisible(el)) {
                return el;
            }
        }

        const images = Array.from(this.container.querySelectorAll('img')).filter((img) => this.isElementVisible(img));
        return images.length ? images[images.length - 1] : null;
    }

    getDocumentTop(rect) {
        const scrollY = window.scrollY || window.pageYOffset || 0;
        return (rect?.top || 0) + scrollY;
    }

    getContainerDocumentTop() {
        if (!this.container) return 0;
        const rect = this.container.getBoundingClientRect();
        return this.getDocumentTop(rect);
    }

    computeMediaState(mediaElement, windowHeight) {
        if (!mediaElement) {
            return {
                mode: 'none',
                shouldSwitchToAbsolute: false,
                rect: null,
                threshold: null
            };
        }

        const rect = mediaElement.getBoundingClientRect();
        const isSlider = mediaElement.classList.contains('mobile-slider-container') || mediaElement.classList.contains('mobile-image-slider');
        const threshold = isSlider ? 0 : windowHeight;
        const tolerance = isSlider ? 0 : 1;
        const shouldSwitchToAbsolute = rect.bottom <= (threshold - tolerance);

        return {
            mode: isSlider ? 'slider' : 'gallery',
            shouldSwitchToAbsolute,
            rect,
            threshold
        };
    }

    updateTitlePosition() {
        if (this.title && this.isTitleSticky) {
            // Only recalculate if title is currently in fixed state
            // Measure the actual visual position when fixed
            const titleRect = this.title.getBoundingClientRect();
            this.titleFixedPosition = titleRect.top;
        }
    }

    /**
     * Compute the container-relative top offset that vertically centres
     * an element of the given height on the last gallery image.
     * Returns null if no last image can be found.
     */
    getLastImageCenteredOffset(elementHeight) {
        const lastImage = this.container.querySelector('.custom-image-item:last-child');
        if (!lastImage) return null;

        const lastImageRect = lastImage.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();

        // Last image position relative to the container
        const lastImageTopInContainer = lastImageRect.top - containerRect.top;
        const lastImageCenter = lastImageTopInContainer + lastImageRect.height / 2;

        // Centre the element on the last image
        return Math.max(0, lastImageCenter - elementHeight / 2);
    }

    updateFixedReferencePositions() {
        const containerRect = this.container ? this.container.getBoundingClientRect() : null;

        if (this.tabs) {
            const tabsRect = this.tabs.getBoundingClientRect();
            this.tabsFixedPosition = tabsRect.top;
            if (containerRect) {
                this.tabsFixedOffset = tabsRect.top - containerRect.top;
            }
        }

        if (this.productInfo) {
            const productInfoRect = this.productInfo.getBoundingClientRect();
            this.productInfoFixedPosition = productInfoRect.top;
        }

        if (this.title) {
            const titleRect = this.title.getBoundingClientRect();
            this.titleFixedPosition = titleRect.top;
            this.titleHeight = titleRect.height;
        }
    }

    checkPositions() {
        const containerRect = this.container.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const mediaElement = this.getActiveMediaElement();

        // If no media element is found, fall back to using the container itself.
        // This prevents elements from getting stuck in fixed or absolute mode
        // when images are lazy-loading or the gallery hasn't rendered yet.
        const mediaState = mediaElement
            ? this.computeMediaState(mediaElement, windowHeight)
            : this.computeMediaState(this.container, windowHeight);
        const shouldSwitchToAbsolute = mediaState.shouldSwitchToAbsolute;

        // For each element, check if we need to switch positioning
        if (shouldSwitchToAbsolute) {
            // Switch to absolute positioning - elements should scroll with content
            if (this.isTabsSticky) {
                this.makeTabsAbsolute();
            }
            if (this.isProductInfoSticky) {
                this.makeProductInfoAbsolute();
            }
            if (this.title && this.isTitleSticky) {
                this.makeTitleAbsolute();
            }
        } else {
            // Switch to fixed positioning - elements should stay in viewport
            if (!this.isTabsSticky) {
                this.makeTabsFixed();
            }
            if (!this.isProductInfoSticky) {
                this.makeProductInfoFixed();
            }
            if (this.title && !this.isTitleSticky) {
                this.makeTitleFixed();
            }
        }
    }

    makeTabsAbsolute() {
        if (!this.isTabsSticky) return;

        const containerRect = this.container.getBoundingClientRect();
        const tabsRect = this.tabs.getBoundingClientRect();
        const containerDocTop = this.getContainerDocumentTop();
        const tabsDocTop = this.getDocumentTop(tabsRect);
        const expectedViewportTop = tabsRect.top;
        let offsetFromContainerTop = tabsDocTop - containerDocTop;
        const containerHeight = containerRect.height;
        const tabsHeight = tabsRect.height;
        // Clamp to the position that centres the element on the last image,
        // not the container bottom.  This ensures reload-at-bottom positions
        // the tabs at the same place as a normal scroll.
        const maxOffset = this.getLastImageCenteredOffset(tabsHeight)
            ?? Math.max(0, containerHeight - tabsHeight);

        offsetFromContainerTop = Math.min(offsetFromContainerTop, maxOffset);
        offsetFromContainerTop = Math.max(0, offsetFromContainerTop);
        const nearTopClamp = Math.abs(offsetFromContainerTop) < 0.5;
        const nearBottomClamp = Math.abs(offsetFromContainerTop - maxOffset) < 0.5;

        this.tabsFixedPosition = tabsRect.top;

        this.isTabsSticky = false;

        // Force immediate positioning without any delays
        this.tabs.style.transition = 'none';
        this.tabs.style.animation = 'none';
        this.tabs.style.position = 'absolute';
        this.tabs.style.top = `${offsetFromContainerTop}px`;
        this.tabs.style.bottom = 'auto';
        this.tabs.style.left = this.originalTabsStyles.left;
        this.tabs.style.right = this.originalTabsStyles.right;
        this.tabs.style.transform = 'none';

        // Add a class for any additional styling
        this.tabs.classList.add('positioned-absolute');

        if (!nearTopClamp && !nearBottomClamp) {
            this.correctAbsolutePosition(this.tabs, expectedViewportTop);
        }
    }

    makeTabsFixed() {
        if (this.isTabsSticky) return;

        this.isTabsSticky = true;

        // Force immediate positioning without any delays
        this.tabs.style.transition = 'none';
        this.tabs.style.animation = 'none';
        this.tabs.style.position = 'fixed';
        this.tabs.style.top = this.originalTabsStyles.top;
        this.tabs.style.bottom = this.originalTabsStyles.bottom;
        this.tabs.style.left = this.originalTabsStyles.left;
        this.tabs.style.right = this.originalTabsStyles.right;
        this.tabs.style.transform = this.originalTabsStyles.transform;

        // Remove the absolute positioning class
        this.tabs.classList.remove('positioned-absolute');

        this.updateFixedReferencePositions();
    }

    makeProductInfoAbsolute() {
        if (!this.isProductInfoSticky) return;

        const containerRect = this.container.getBoundingClientRect();
        const productInfoRect = this.productInfo.getBoundingClientRect();
        const containerDocTop = this.getContainerDocumentTop();
        const productDocTop = this.getDocumentTop(productInfoRect);
        const expectedViewportTop = productInfoRect.top;
        let offsetFromContainerTop = productDocTop - containerDocTop;
        const containerHeight = containerRect.height;
        const productInfoHeight = productInfoRect.height;
        // Clamp to the position that centres the element on the last image,
        // not the container bottom.
        const maxOffset = this.getLastImageCenteredOffset(productInfoHeight)
            ?? Math.max(0, containerHeight - productInfoHeight);

        offsetFromContainerTop = Math.min(offsetFromContainerTop, maxOffset);
        offsetFromContainerTop = Math.max(0, offsetFromContainerTop);
        const nearTopClamp = Math.abs(offsetFromContainerTop) < 0.5;
        const nearBottomClamp = Math.abs(offsetFromContainerTop - maxOffset) < 0.5;

        this.productInfoFixedPosition = productInfoRect.top;

        this.isProductInfoSticky = false;

        // Force immediate positioning without any delays
        this.productInfo.style.transition = 'none';
        this.productInfo.style.animation = 'none';
        this.productInfo.style.position = 'absolute';
        this.productInfo.style.top = `${offsetFromContainerTop}px`;
        this.productInfo.style.bottom = 'auto';
        this.productInfo.style.right = this.originalProductInfoStyles.right;
        this.productInfo.style.left = this.originalProductInfoStyles.left;
        this.productInfo.style.transform = 'none';
        this.productInfo.style.willChange = 'auto';
        this.productInfo.style.backfaceVisibility = 'visible';

        // Add a class for any additional styling
        this.productInfo.classList.add('positioned-absolute');

        if (!nearTopClamp && !nearBottomClamp) {
            this.correctAbsolutePosition(this.productInfo, expectedViewportTop);
        }
    }

    makeProductInfoFixed() {
        if (this.isProductInfoSticky) return;

        this.isProductInfoSticky = true;

        // Force immediate positioning without any delays
        this.productInfo.style.transition = 'none';
        this.productInfo.style.animation = 'none';
        this.productInfo.style.position = 'fixed';
        this.productInfo.style.top = this.originalProductInfoStyles.top;
        this.productInfo.style.bottom = this.originalProductInfoStyles.bottom;
        this.productInfo.style.right = this.originalProductInfoStyles.right;
        this.productInfo.style.left = this.originalProductInfoStyles.left;
        this.productInfo.style.transform = this.originalProductInfoStyles.transform;
        this.productInfo.style.willChange = 'auto';
        this.productInfo.style.backfaceVisibility = 'visible';

        // Remove the absolute positioning class
        this.productInfo.classList.remove('positioned-absolute');

        this.updateFixedReferencePositions();
    }

    makeTitleAbsolute() {
        if (!this.title || !this.isTitleSticky) return;

        this.isTitleSticky = false;

        // Switch .product-title-bar from sticky → relative so it scrolls
        // away with the page content instead of staying pinned at the top.
        //
        // During normal scrolling the current scrollY IS the switch point,
        // so the bar stays at the viewport top and then scrolls away.
        // On a reload-at-bottom, scrollY is far past the gallery, so we
        // cap it at the scroll position where the gallery bottom would have
        // crossed the viewport bottom — the true switch point.
        if (this.titleBar) {
            var scrollY = window.scrollY || window.pageYOffset || 0;
            var topValue = scrollY;

            var lastImage = this.container.querySelector('.custom-image-item:last-child');
            if (lastImage) {
                var lastImageRect = lastImage.getBoundingClientRect();
                // Gallery bottom in document coordinates
                var galleryBottomDoc = lastImageRect.bottom + scrollY;
                // ScrollY at which the gallery bottom would hit the viewport bottom
                var switchScrollY = galleryBottomDoc - window.innerHeight;
                topValue = Math.min(scrollY, switchScrollY);
            }

            this.titleBar.style.position = 'relative';
            this.titleBar.style.top = topValue + 'px';
        }
    }

    makeTitleFixed() {
        if (!this.title || this.isTitleSticky) return;

        this.isTitleSticky = true;

        // Restore sticky positioning so the breadcrumb pins at the viewport top
        if (this.titleBar) {
            this.titleBar.style.position = 'sticky';
            this.titleBar.style.top = '0px';
        }

        this.updateFixedReferencePositions();
    }

    correctAbsolutePosition(element, expectedViewportTop) {
        if (!element) return;

        // Apply correction synchronously to avoid race conditions with scroll events.
        // Using RAF here previously allowed checkPositions() to fire before the
        // correction was applied, causing elements to jump or disappear.
        element.offsetHeight; // Force reflow so getBoundingClientRect reflects the new position
        const rect = element.getBoundingClientRect();
        const delta = typeof expectedViewportTop === 'number' ? rect.top - expectedViewportTop : 0;

        if (Math.abs(delta) < 0.5) {
            return;
        }

        const currentTop = parseFloat(element.style.top || '0');
        if (Number.isNaN(currentTop)) {
            return;
        }

        element.style.top = `${currentTop - delta}px`;
    }

    destroy() {
        // Remove event listeners using the bound references
        if (this.boundCheckPositions) {
            window.removeEventListener('scroll', this.boundCheckPositions);
        }
        if (this.boundHandleResize) {
            window.removeEventListener('resize', this.boundHandleResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this.boundHandleResize);
            }
        }

        // Clear all inline styles to let CSS media queries take over
        if (this.tabs) {
            this.clearInlineStyles(this.tabs, ['position', 'top', 'bottom', 'left', 'right', 'transform', 'transition', 'animation']);
            this.tabs.classList.remove('positioned-absolute');
        }

        if (this.productInfo) {
            this.clearInlineStyles(this.productInfo, ['position', 'top', 'bottom', 'left', 'right', 'transform', 'transition', 'animation', 'willChange', 'backfaceVisibility']);
            this.productInfo.classList.remove('positioned-absolute');
        }

        if (this.title) {
            this.clearInlineStyles(this.title, ['position', 'top', 'bottom', 'left', 'right', 'transform', 'transition', 'animation', 'padding', 'zIndex', 'mixBlendMode', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft']);
            this.title.classList.remove('positioned-absolute');
            this.title.classList.remove('breadcrumb--scrolled-past');
        }

        // Restore the titleBar to sticky at top (theme.liquid default)
        if (this.titleBar) {
            this.titleBar.style.position = 'sticky';
            this.titleBar.style.top = '0px';
        }
    }
}

// Handle responsive initialization
(function () {
    let controllerInstance = null;
    let resizeTimeout = null;
    let lastKnownViewportState = null; // Track if we were desktop or mobile

    function isDesktopViewport() {
        const screenWidth = getZoomAdjustedViewportWidth();
        return screenWidth >= STICKY_BREAKPOINT;
    }

    function initController() {
        const container = document.querySelector('.custom-product-container');
        const isDesktop = isDesktopViewport();

        if (isDesktop && container && !controllerInstance) {
            controllerInstance = new StickyElementsController();
            window.stickyElementsController = controllerInstance;
            lastKnownViewportState = 'desktop';
        } else if (!isDesktop) {
            // Not desktop viewport; nothing to initialize
        } else if (!container) {
            // Container missing; nothing to initialize
        } else if (controllerInstance) {
            // Instance already exists; skip
        }
    }

    function destroyController() {
        const isDesktop = isDesktopViewport();

        if (!isDesktop && controllerInstance) {
            if (typeof controllerInstance.destroy === 'function') {
                controllerInstance.destroy();
            }
            controllerInstance = null;
            window.stickyElementsController = null;
            lastKnownViewportState = 'mobile';
        }
    }

    function handleResize() {
        // Clear any pending resize operations
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }

        // Debounce resize events to avoid rapid destroy/init cycles during zoom
        resizeTimeout = setTimeout(() => {
            const isDesktop = isDesktopViewport();
            const currentState = isDesktop ? 'desktop' : 'mobile';

            // Only take action if viewport state actually changed (not just zoom)
            if (currentState !== lastKnownViewportState) {
                if (isDesktop) {
                    initController();
                } else {
                    destroyController();
                }
            } else {
                // If controller exists and we're still in the same state, just trigger handleResize
                if (controllerInstance && typeof controllerInstance.handleResize === 'function') {
                    controllerInstance.handleResize();
                }
            }
        }, 250); // 250ms debounce
    }

    document.addEventListener('DOMContentLoaded', () => {
        initController();
        window.addEventListener('resize', handleResize, { passive: true });
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleResize, { passive: true });
        }
    });
})();
